import { planExport } from "@/lib/exportCharge";
import { canStartUse, confirmAtEdge, hydrateUsage, isToolSessionCounted, peekUsage, refreshUsageBounded, type UsageFetcher } from "@/lib/usageGate";

// THE export gate's decision, in one place. Every way a result leaves a Premium tool (CSV, Excel, PDF, image,
// ICS, JSON backup, ZIP) must pass through it:
//   1. BEFORE the file is built, a tool asks `decideExport` (it takes no use). Blocked means: open the existing
//      paywall dialog and build nothing.
//   2. The file itself leaves through downloadBlob (components/ToolLayout), which asks the same gate again and
//      takes the visit's one use at that moment, only when the file is actually saved.
// So a use is never charged for a file that was not delivered, and a file is never delivered without the gate.
// client/src/exportGate.guard.test.ts fails the build if a listed tool's export handler skips this.

export type ExportDecision = "allowed" | "blocked";

/** Non-charging. A visit that already counted its use may export again at no cost; otherwise the count decides. */
export async function decideExport(toolId: string, scope: string, fetcher: UsageFetcher): Promise<ExportDecision> {
  if (isToolSessionCounted(toolId)) return "allowed";
  hydrateUsage(scope);
  const known = peekUsage(scope);
  if (!known) return (await canStartUse(scope, fetcher)) ? "allowed" : "blocked"; // first answer, bounded
  // A cached "blocked" is confirmed once (an upgrade or a new day changes it); a cached "allowed" on the last use
  // is confirmed too, so a stale cache never hands out a file the server has already counted. One wait at most.
  if (!known.allowed) await refreshUsageBounded(scope, fetcher);
  else await confirmAtEdge(scope, fetcher);
  return peekUsage(scope)?.allowed ? "allowed" : "blocked";
}

/**
 * Non-charging, for tools where the person edits and delivers again in one visit (keys: lib/exportCharge). The exact
 * output already delivered in this visit is allowed free; the visit's first output follows the normal rule; a
 * changed output needs a use to be left, so it is blocked (paywall, nothing delivered) at 0 remaining.
 */
export async function decideExportKeyed(toolId: string, scope: string, key: string, fetcher: UsageFetcher): Promise<ExportDecision> {
  const plan = planExport(toolId, key);
  if (plan === "free") return "allowed";
  if (plan === "first") return decideExport(toolId, scope, fetcher);
  hydrateUsage(scope);
  if (!peekUsage(scope)) return (await canStartUse(scope, fetcher)) ? "allowed" : "blocked";
  await confirmAtEdge(scope, fetcher);
  return peekUsage(scope)?.allowed ? "allowed" : "blocked";
}

/** The text shown next to export buttons, or null when the person has no trial count to show (paid, admin, unknown). */
export function remainingLabel(scope: string): string | null {
  const s = peekUsage(scope);
  if (!s || s.isPro || s.isAdmin) return null;
  return `${Math.max(0, s.limit - s.usageToday)} remaining today`;
}
